import {
  backfillImportTimeWindow,
  resolvePickupDeliveryBackfill,
  type ImportTimeTolerance,
} from "./import-time-window";

/**
 * 注文の指定時刻が「片側だけ」入っているかの判定と、その一括補完の可否 (#657 / #677 / #754)。
 *
 * 片側だけの指定は、最適化の枠解決 (`resolveWindowWithFit`) が空いている側を**便区分窓の端まで
 * 広げる**ため、指定から数時間ずれた配達が「制約充足解」として成立する。取込時の補完
 * (`backfillImportTimeWindow`) が入る前に取り込まれた注文はこの状態のまま残る。
 *
 * **同じ規則を 3 か所が見る** (#754 でここに集約):
 * 1. api の一括補完ハンドラ (`order.backfillPartialTimeWindows`) — 何を更新するか
 * 2. api の最適化ペイロード (`optimization-payload.ts`) — `*_window_one_sided` 警告を出すか
 * 3. admin の受注一覧 — 一括補完の導線に何件と出すか
 *
 * 3 か所に別実装があると、最適化が「N 件あります」と警告しているのに一覧は 0 件、
 * 一覧が「N 件補完できます」と出すのに押しても減らない、が起きる。
 *
 * 秒 (最適化) と "HH:MM" 文字列 (受注 DTO) のどちらでも同じ判定になるよう両方を受ける。
 * 空文字・空白のみは「未入力」として扱う (DB には空文字が入りうる)。
 */

/** 時刻値が入っているか。null / undefined / 空白のみは未入力。 */
function hasTimeValue(value: string | number | null | undefined): boolean {
  if (value == null) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
}

/** 開始・終了のどちらか一方だけが指定されている (両方あり / 両方なしは false)。 */
export function isOneSidedTimeWindow(
  start: string | number | null | undefined,
  end: string | number | null | undefined,
): boolean {
  return hasTimeValue(start) !== hasTimeValue(end);
}

/** 配達・集荷の "HH:MM" フィールド (order CRUD / 一覧 DTO の共通部分)。 */
export interface OrderTimeWindowFields {
  delivery_time_start?: string | null;
  delivery_time_end?: string | null;
  pickup_time_start?: string | null;
  pickup_time_end?: string | null;
}

const ORDER_TIME_WINDOW_KEYS = [
  "delivery_time_start",
  "delivery_time_end",
  "pickup_time_start",
  "pickup_time_end",
] as const;

/** 配達・集荷のどちらかが片側だけの指定になっているか。 */
export function orderHasPartialTimeWindow(fields: OrderTimeWindowFields): boolean {
  return (
    isOneSidedTimeWindow(fields.delivery_time_start, fields.delivery_time_end) ||
    isOneSidedTimeWindow(fields.pickup_time_start, fields.pickup_time_end)
  );
}

/**
 * 片側だけ指定の時刻を取込と同じ許容幅で時間幅に補完する (#677)。
 * 取込 (#657) と同じ `backfillImportTimeWindow` を使い、抽出済みの時刻は書き換えない。
 *
 * **補完が「集荷開始 >= 配達開始」を作る場合は最小限だけ取り消す** (`resolvePickupDeliveryBackfill`)。
 * contract の `refinePickupBeforeDelivery` が 400 を返すため、素通しすると受注編集フォームから
 * 二度と保存できない受注ができる。取込だけがこの規則を持ち、単件 CRUD と一括補完が
 * 素通しだった (#754)。
 *
 * `undefined` のフィールド (= 呼び出しが送っていない項目) は触らない。部分更新の入力に
 * そのまま重ねられるようにするため。
 */
export function backfillOrderTimeFields(
  fields: OrderTimeWindowFields,
  tolerance: ImportTimeTolerance,
): OrderTimeWindowFields {
  const result: OrderTimeWindowFields = { ...fields };
  const hasDelivery =
    fields.delivery_time_start !== undefined || fields.delivery_time_end !== undefined;
  const hasPickup = fields.pickup_time_start !== undefined || fields.pickup_time_end !== undefined;
  // 前後関係の判定には両方の枠が要るため、送られていない側も含めて解決してから
  // 「送られた側だけ」書き戻す。
  const { delivery, pickup } = resolvePickupDeliveryBackfill(
    backfillImportTimeWindow(fields.delivery_time_start, fields.delivery_time_end, tolerance),
    backfillImportTimeWindow(fields.pickup_time_start, fields.pickup_time_end, tolerance),
  );
  if (hasDelivery) {
    result.delivery_time_start = delivery.start;
    result.delivery_time_end = delivery.end;
  }
  if (hasPickup) {
    result.pickup_time_start = pickup.start;
    result.pickup_time_end = pickup.end;
  }
  return result;
}

/**
 * 補完の前後で時刻フィールドが 1 つでも変わったか (#754)。
 *
 * 許容幅 0 や日の端の丸め (納品期限 00:00 から早着分を引いても 00:00) では
 * `backfillOrderTimeFields` が入力をそのまま返す。呼び出し側がこれを見ずに update すると
 * 「補完しました」と言いながら値が変わらず、一覧の件数が減らないまま押し続けさせてしまう。
 *
 * **空文字・空白のみと null の違いは「変わった」に数えない。** 補完側は正規化して null を返すため、
 * DB に空文字が入っている受注では正規化だけで差分が立つ。それを補完扱いにすると「1 件補完しました」
 * と出るのに片側指定のままで、まさに件数が減らない状態になる (2 回目の実行で初めて対象外になる)。
 */
export function orderTimeFieldsChanged(
  before: OrderTimeWindowFields,
  after: OrderTimeWindowFields,
): boolean {
  return ORDER_TIME_WINDOW_KEYS.some(
    (key) => normalizeTimeField(before[key]) !== normalizeTimeField(after[key]),
  );
}

/** 空文字・空白のみを未入力 (null) に寄せる。比較の土俵を補完側の正規化に合わせる。 */
function normalizeTimeField(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/**
 * その受注が一括補完で**実際に値が変わる**か (#754)。
 *
 * 片側指定であっても許容幅 0 や日の端の丸めでは埋められない。画面はこの判定で数えることで
 * 「押しても減らない件数」を出さずに済む (api の更新対象と同じ規則)。
 */
export function canBackfillOrderTimeWindow(
  fields: OrderTimeWindowFields,
  tolerance: ImportTimeTolerance,
): boolean {
  if (!orderHasPartialTimeWindow(fields)) return false;
  return orderTimeFieldsChanged(fields, backfillOrderTimeFields(fields, tolerance));
}

/** 一覧から一括補完で値が変わる注文だけを数える (導線に出す件数)。 */
export function countBackfillableOrders(
  orders: readonly OrderTimeWindowFields[],
  tolerance: ImportTimeTolerance,
): number {
  return orders.reduce(
    (count, order) => (canBackfillOrderTimeWindow(order, tolerance) ? count + 1 : count),
    0,
  );
}

/** 一覧から片側指定の注文だけを数える (補完可否は問わない)。 */
export function countPartialTimeWindowOrders(orders: readonly OrderTimeWindowFields[]): number {
  return orders.reduce((count, order) => (orderHasPartialTimeWindow(order) ? count + 1 : count), 0);
}

/**
 * 配達締切前の到着余裕の既定値 (分, #855)。DB カラムの DEFAULT / seed / 管理画面の初期値の単一源泉。
 * 現場は「お届け時間の 15 分前までに着く」前提で動いている。
 */
export const DELIVERY_ARRIVAL_MARGIN_DEFAULT_MINUTES = 15;

/**
 * 配達の時間窓に到着余裕を織り込む (#855)。締切 = 終了 − 余裕。
 *
 * 時間幅が余裕以下でも締切を守る: 11:50〜12:00・余裕 15 分 → 11:45 の 1 点
 * (指定の開始より早く着くことを許す)。1 点の窓より早く着いた場合は現地で待つ。
 *
 * 余裕が無いと、現地待機コスト (#725) を減らすために solver がデポ出発を遅らせ、
 * 配達が終了時刻ちょうどに並ぶ (2026-09-03 本番: 31 件中 17 件が終了 0〜4 分前)。
 *
 * 呼出側は**終了時刻が指定されている配達窓だけ**に適用すること (便区分の既定窓には適用しない)。
 */
export function applyDeliveryArrivalMargin<
  T extends { start_seconds: number; end_seconds: number },
>(window: T, marginSeconds: number): T {
  if (marginSeconds <= 0) return window;
  const end = Math.max(0, window.end_seconds - marginSeconds);
  return { ...window, start_seconds: Math.min(window.start_seconds, end), end_seconds: end };
}
